import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { SEO } from "@/components/SEO";

function AuthShell({ children, title }) {
  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-[2rem] border border-[#EAE5DF] p-8 sm:p-10 shadow-xl">
        <img src={LOGO_URL} alt="The Velvet Brush" className="h-14 w-auto mx-auto" />
        <h1 className="mt-6 font-serif font-bold text-ink text-2xl text-center">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Admin Login">
      <SEO title="Admin Login" description="The Velvet Brush admin panel." path="/admin/login" />
      <form onSubmit={submit} data-testid="admin-login-form" className="mt-6 flex flex-col gap-4">
        <div>
          <Label htmlFor="admin-email">Email</Label>
          <Input id="admin-email" data-testid="admin-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} className="rounded-xl h-12" placeholder="admin@thevelvetbrush.com" />
        </div>
        <div>
          <Label htmlFor="admin-password">Password</Label>
          <Input id="admin-password" data-testid="admin-password-input" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-12" placeholder="••••••••" />
        </div>
        {error && <p data-testid="admin-login-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}
        <Button type="submit" data-testid="admin-login-submit" disabled={loading}
          className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12 active:scale-95 transition-transform">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
        </Button>
        <Link to="/forgot-password" data-testid="forgot-password-link" className="text-center text-sm text-velvet hover:text-velvethover font-semibold">
          Forgot password?
        </Link>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {}
    setDone(true);
    setLoading(false);
  };

  return (
    <AuthShell title="Reset Password">
      <SEO title="Forgot Password" description="Reset your admin password." path="/forgot-password" />
      {done ? (
        <p data-testid="forgot-password-confirmation" className="mt-6 text-sm text-ink/70 text-center leading-relaxed">
          If that email is registered, a reset link has been sent.
        </p>
      ) : (
        <form onSubmit={submit} data-testid="forgot-password-form" className="mt-6 flex flex-col gap-4">
          <div>
            <Label htmlFor="forgot-email">Admin Email</Label>
            <Input id="forgot-email" data-testid="forgot-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} className="rounded-xl h-12" />
          </div>
          <Button type="submit" data-testid="forgot-submit-btn" disabled={loading}
            className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
          </Button>
        </form>
      )}
      <Link to="/admin/login" data-testid="back-to-signin" className="mt-4 block text-center text-sm text-velvet hover:text-velvethover font-semibold">
        Back to sign in
      </Link>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Choose a New Password">
      <SEO title="Reset Password" description="Set a new admin password." path="/reset-password" />
      {done ? (
        <p data-testid="reset-success" className="mt-6 text-sm text-ink/70 text-center">Password updated. Redirecting to sign in…</p>
      ) : (
        <form onSubmit={submit} data-testid="reset-password-form" className="mt-6 flex flex-col gap-4">
          <div>
            <Label htmlFor="new-password">New Password</Label>
            <Input id="new-password" data-testid="new-password-input" type="password" required minLength={6} value={password}
              onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-12" />
          </div>
          {error && <p data-testid="reset-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}
          <Button type="submit" data-testid="reset-submit-btn" disabled={loading || !token}
            className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update Password"}
          </Button>
        </form>
      )}
      <Link to="/admin/login" data-testid="reset-back-to-signin" className="mt-4 block text-center text-sm text-velvet hover:text-velvethover font-semibold">
        Back to sign in
      </Link>
    </AuthShell>
  );
}
